import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useAuthContext } from './AuthContext';
import { observeNewTickets, getTicketDetails, updateTicketStatus, TICKET_STATUS } from '../services/supportService';
import { collectionGroup, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../services/firebase/firebase';
import { observeAdminNotifications, markAdminNotificationsRead } from '../services/adminNotificationsService';

// Créer le contexte
const NotificationContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useNotifications = () => useContext(NotificationContext);

// Fournisseur du contexte
export const NotificationProvider = ({ children }) => {
  const { currentUser, isAdmin } = useAuthContext();
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState(new Set());
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  
  // Réinitialiser le compteur de nouveaux tickets (ne plus l'utiliser automatiquement)
  const resetNewTicketsCount = useCallback(() => {
    console.log('Réinitialisation manuelle du compteur de tickets');
    setNewTicketsCount(0);
    setUnreadTickets(new Set());
  }, []);
  
  // Marquer un ticket spécifique comme traité
  const markTicketAsRead = useCallback((ticketId) => {
    console.log('Marquage du ticket comme traité:', ticketId);
    setUnreadTickets(prev => {
      const newSet = new Set(prev);
      newSet.delete(ticketId);
      return newSet;
    });
    
    // Mettre à jour le compteur
    setNewTicketsCount(prev => Math.max(0, prev - 1));
  }, []);
  
  // Marquer des notifications admin (chat demande / réponse support) comme lues
  const markAdminNotifs = useCallback(async (predicate) => {
    const ids = adminNotifications
      .filter((n) => !n.read && predicate(n))
      .map((n) => n.id);
    if (ids.length === 0) return;
    await markAdminNotificationsRead(ids);
    setAdminNotifications((prev) =>
      prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n))
    );
  }, [adminNotifications]);

  const markRequestNotifsRead = useCallback(
    () => markAdminNotifs((n) => n.type === 'request_message'),
    [markAdminNotifs]
  );

  const markTicketReplyNotifsRead = useCallback(
    () => markAdminNotifs((n) => n.type === 'ticket_reply'),
    [markAdminNotifs]
  );

  const markAllAdminNotificationsRead = useCallback(
    () => markAdminNotifs(() => true),
    [markAdminNotifs]
  );

  // Marquage ciblé (utilisé à l'ouverture d'un détail) pour préserver les pastilles
  // des autres items tant qu'ils ne sont pas ouverts.
  const markRequestNotifsReadForUser = useCallback(async (userId) => {
    await markAdminNotifs((n) => n.type === 'request_message' && n.userId === userId);
  }, [markAdminNotifs]);

  const markTicketReplyNotifsReadForTicket = useCallback(async (userId, ticketId) => {
    await markAdminNotifs(
      (n) => n.type === 'ticket_reply' && n.userId === userId && n.ticketId === ticketId
    );
  }, [markAdminNotifs]);


  // Ajouter une notification récente
  const addRecentNotification = useCallback((notification) => {
    console.log('Ajout d\'une nouvelle notification:', notification.title);
    setRecentNotifications(prev => [notification, ...prev].slice(0, 10));
  }, []);

  // Observer les notifications « centre admin » (messages clients création/support)
  useEffect(() => {
    if (!currentUser || !isAdmin) {
      setAdminNotifications([]);
      return undefined;
    }
    return observeAdminNotifications(setAdminNotifications);
  }, [currentUser, isAdmin]);


  // Observer les nouveaux tickets
  useEffect(() => {
    let unsubscribe = () => {};
    
    const setupObserver = () => {
      if (!currentUser || !isAdmin) return;
      
      console.log('Configuration de l\'observateur de tickets pour l\'admin:', currentUser.email);
      
      unsubscribe = observeNewTickets((newTickets) => {
        console.log('Nouveaux tickets détectés:', newTickets.length);
        
        // Filtrer pour n'obtenir que les tickets qui n'ont pas encore été notifiés
        const newUnreadTickets = new Set(unreadTickets);
        const trulyNewTickets = newTickets.filter(ticket => !newUnreadTickets.has(ticket.id));
        
        if (trulyNewTickets.length > 0) {
          console.log('Nouveaux tickets non notifiés:', trulyNewTickets.length);
          
          // Ajouter les nouveaux tickets à la liste des non lus
          trulyNewTickets.forEach(ticket => {
            newUnreadTickets.add(ticket.id);
          });
          
          setUnreadTickets(newUnreadTickets);
          
          // Mettre à jour le compteur
          setNewTicketsCount(newUnreadTickets.size);
          
          // Ajouter les nouvelles notifications
          trulyNewTickets.forEach(ticket => {
            // Créer un objet notification
            const notification = {
              id: ticket.id,
              title: 'Nouveau ticket',
              message: `${ticket.userInfo?.displayName || 'Un utilisateur'} a créé un ticket: "${ticket.title}"`,
              type: 'ticket',
              data: ticket,
              timestamp: new Date()
            };
            
            // Ajouter à la liste des notifications récentes 
            addRecentNotification(notification);
            
            // Suppression du bloc toast.info() qui affichait les notifications
          });
        }
      });
    };
    
    // Petit délai pour s'assurer que tout est initialisé
    const timeoutId = setTimeout(() => {
      setupObserver();
    }, 1000);
    
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [currentUser, isAdmin, unreadTickets, addRecentNotification]);

  // Observer les changements de statut des tickets non lus
  useEffect(() => {
    if (!currentUser || !isAdmin || unreadTickets.size === 0) return;
    
    // Pour chaque ticket non lu, vérifier son statut à intervalles réguliers
    const checkTicketsInterval = setInterval(async () => {
      const ticketsToCheck = Array.from(unreadTickets);
      console.log(`Vérification du statut de ${ticketsToCheck.length} tickets non lus`);
      
      for (const ticketId of ticketsToCheck) {
        try {
          // Nous devons extraire l'userId à partir de l'ID du ticket ou d'une autre source
          // Comme nous n'avons pas cette information facilement, nous pourrions:
          // 1. Stocker les tickets complets dans unreadTickets au lieu de juste les IDs
          // 2. Ou faire une requête supplémentaire pour trouver le ticket
          
          // Cette partie est une solution temporaire et pourrait être améliorée
          const ticketsRef = collectionGroup(firestore, 'support_tickets');
          const q = query(ticketsRef, where('__name__', '==', ticketId));
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const ticketDoc = snapshot.docs[0];
            const userId = ticketDoc.ref.path.split('/')[1]; // Extract userId from path
            const ticket = await getTicketDetails(userId, ticketId);
            
            // Si le ticket n'est plus NEW, le marquer comme traité
            if (ticket.status !== TICKET_STATUS.NEW) {
              markTicketAsRead(ticketId);
            }
          } else {
            // Si on ne trouve pas le ticket, le marquer comme traité
            markTicketAsRead(ticketId);
          }
        } catch (error) {
          console.error(`Erreur lors de la vérification du ticket ${ticketId}:`, error);
        }
      }
    }, 60000); // Vérifier chaque minute
    
    return () => clearInterval(checkTicketsInterval);
  }, [currentUser, isAdmin, unreadTickets, markTicketAsRead]);
  
  // Fournir les valeurs du contexte
  const requestUnreadCount = adminNotifications.filter(
    (n) => n.type === 'request_message' && !n.read
  ).length;
  const ticketReplyUnreadCount = adminNotifications.filter(
    (n) => n.type === 'ticket_reply' && !n.read
  ).length;
  const totalAdminUnread = requestUnreadCount + ticketReplyUnreadCount;

  // Dérivés « par item » pour les pastilles dans les listes (demandes / tickets)
  const unreadRequestUserIds = useMemo(() => Array.from(new Set(
    adminNotifications
      .filter((n) => n.type === 'request_message' && !n.read && n.userId)
      .map((n) => n.userId)
  )), [adminNotifications]);

  const unreadTicketKeys = useMemo(() => {
    const keys = [];
    const seen = new Set();
    for (const n of adminNotifications) {
      if (n.type === 'ticket_reply' && !n.read && n.userId && n.ticketId) {
        const key = `${n.userId}::${n.ticketId}`;
        if (!seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      }
    }
    return keys;
  }, [adminNotifications]);

  const value = {
    newTicketsCount,
    resetNewTicketsCount,
    markTicketAsRead,
    unreadTickets: Array.from(unreadTickets),
    recentNotifications,
    adminNotifications,
    requestUnreadCount,
    ticketReplyUnreadCount,
    totalAdminUnread,
    markRequestNotifsRead,
    markTicketReplyNotifsRead,
    markAllAdminNotificationsRead,
    unreadRequestUserIds,
    unreadTicketKeys,
    markRequestNotifsReadForUser,
    markTicketReplyNotifsReadForTicket
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};